/*
 * Gráfica de barras verticales, réplica de Chart.js (tipo bar) del HTML:
 * ejes con ticks en COP, gridlines, animación de entrada, hover y tooltip.
 */
import { useEffect, useRef, useState } from "react";

import Tooltipx from "./Tooltipx";
import { buildTicks, fmtCOP, htmlTheme } from "./utils/carthelp";

const AXIS_W = 96;
const XLBL_H = 22;

/**
 * Renderiza una gráfica de barras verticales con ejes y gridlines
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Filas [{ label, value, color, raw }]
 * @param {Function} props.onItemClick Callback con la fila y su índice al hacer click
 * @param {number} props.height Alto total del área de la gráfica
 */
const Vertbars = ({ data = [], onItemClick, height = 260 }) => {
  const { ticks, top } = buildTicks(Math.max(1, ...data.map((mItem) => Math.abs(mItem.value))));
  const bClickable = typeof onItemClick === "function";

  const containerRef = useRef(null);
  const [bMounted, setMounted] = useState(false);
  const [nHover, setHover] = useState(null);
  const [mTip, setTip] = useState(null);

  //Dispara la animación de entrada de las barras
  useEffect(() => {
    const nTimer = setTimeout(() => setMounted(true), 30);
    return () => clearTimeout(nTimer);
  }, []);

  //Actualiza la posición del tooltip siguiendo el mouse
  const handleMove = (oEvent, mItem) => {
    const oRect = containerRef.current?.getBoundingClientRect();
    if (!oRect) return;
    setTip({
      x: oEvent.clientX - oRect.left,
      y: oEvent.clientY - oRect.top,
      title: mItem.raw || mItem.label,
      lines: [fmtCOP(mItem.value)],
    });
  };

  //Limpia el estado de hover y tooltip
  const handleLeave = () => {
    setHover(null);
    setTip(null);
  };

  return (
    <div ref={containerRef} style={{ display: "flex", height, position: "relative" }} onMouseLeave={handleLeave}>
      <Tooltipx tip={mTip} />
      <div style={{ width: AXIS_W, flexShrink: 0, position: "relative" }}>
        {ticks.map((nTick) => (
          <span
            key={nTick}
            style={{
              position: "absolute",
              right: 8,
              bottom: `calc(${XLBL_H}px + (100% - ${XLBL_H}px) * ${nTick / top} - 6px)`,
              fontSize: 10,
              color: htmlTheme.muted,
              whiteSpace: "nowrap",
            }}
          >
            {fmtCOP(nTick)}
          </span>
        ))}
      </div>
      <div style={{ flex: 1, position: "relative" }}>
        {ticks.map((nTick) => (
          <div
            key={nTick}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: `calc(${XLBL_H}px + (100% - ${XLBL_H}px) * ${nTick / top})`,
              borderBottom: `1px solid ${nTick === 0 ? "#D5DAE8" : "#EDEFF7"}`,
            }}
          />
        ))}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: XLBL_H,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-around",
          }}
        >
          {data.map((mItem, nIndex) => (
            <div
              key={nIndex}
              onClick={bClickable ? () => onItemClick(mItem, nIndex) : undefined}
              onMouseEnter={() => setHover(nIndex)}
              onMouseMove={(oEvent) => handleMove(oEvent, mItem)}
              style={{
                width: `${Math.min(60 / Math.max(1, data.length), 14)}%`,
                height: bMounted ? `${(Math.abs(mItem.value) / top) * 100}%` : 0,
                minHeight: bMounted ? 2 : 0,
                background: mItem.color || htmlTheme.navy,
                borderRadius: "6px 6px 0 0",
                cursor: bClickable ? "pointer" : "default",
                transition: "height .6s ease, filter .15s ease",
                filter: nHover === nIndex ? "brightness(1.15)" : "none",
              }}
            />
          ))}
        </div>
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: XLBL_H,
            display: "flex",
            justifyContent: "space-around",
          }}
        >
          {data.map((mItem, nIndex) => (
            <span
              key={nIndex}
              title={mItem.raw || mItem.label}
              style={{
                fontSize: 11,
                color: htmlTheme.muted,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                maxWidth: `${100 / Math.max(1, data.length)}%`,
                textAlign: "center",
                paddingTop: 6,
              }}
            >
              {mItem.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Vertbars;
